import { useState, useEffect } from 'react';

/**
 * Returns the current screen size category.
 * phone:    < 640px   (phone portrait)
 * phablet:  640–767px (large phone / small phone landscape)
 * tablet:   768px+    (tablet portrait or landscape)
 *
 * Uses matchMedia listeners instead of the resize event.
 * matchMedia fires ONLY when a breakpoint threshold is crossed — not on every
 * pixel change and crucially not on every iOS Safari scroll (where the browser
 * chrome showing/hiding was triggering spurious resize events and re-renders).
 */

// ── Breakpoints ────────────────────────────────────────────────────
// Defined at module level so they are created once, not on every render.
const MQ_TABLET  = typeof window !== 'undefined' ? window.matchMedia('(min-width: 768px)') : null;
const MQ_PHABLET = typeof window !== 'undefined' ? window.matchMedia('(min-width: 640px)') : null;

function getSize() {
  // SSR / test environment guard — default to 'phone' when window is unavailable.
  if (typeof window === 'undefined') return 'phone';
  if (MQ_TABLET?.matches)  return 'tablet';
  if (MQ_PHABLET?.matches) return 'phablet';
  return 'phone';
}

export function useScreenSize() {
  const [size, setSize] = useState(getSize);

  useEffect(() => {
    const handler = () => setSize(getSize());

    MQ_TABLET?.addEventListener('change', handler);
    MQ_PHABLET?.addEventListener('change', handler);

    return () => {
      MQ_TABLET?.removeEventListener('change', handler);
      MQ_PHABLET?.removeEventListener('change', handler);
    };
  }, []); // empty deps — handler and MQs are stable module-level references

  return {
    size,
    isPhone:   size === 'phone',
    isPhablet: size === 'phablet',
    isTablet:  size === 'tablet',
    isMobile:  size !== 'tablet', // phone or phablet
  };
}